import { useSearchParams } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { LiveRoster } from "./LiveRoster";
import { HistoricalReport } from "./HistoricalReport";
import { EvictionList } from "@/features/queue-evictions/components/EvictionList";
import "../admin.css";

type TabId = "live" | "historical";

/**
 * R-236 — Staff Status Monitor
 *
 * Route: /admin/operators
 * Permission gate: supervisor.ops_console (inline — defensive depth so a
 * direct-URL visit shows a forbidden message rather than a blank/crash).
 *
 * The active tab lives in the URL (`?tab=live|historical`, default "live")
 * so B2 (live-roster row → Historical pre-filtered to an operator) and B4
 * (R-216 deep links to `/admin/operators?operatorId=…`) can drive tab +
 * operator selection via query params. Switching tabs preserves any other
 * params (e.g. `operatorId`).
 */
export function StaffMonitorPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const canView = user?.permissions?.includes("supervisor.ops_console") ?? false;

  const activeTab: TabId = searchParams.get("tab") === "historical" ? "historical" : "live";

  function selectTab(tab: TabId) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      },
      { replace: true },
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1 className="admin-title" role="heading" aria-level={1}>
          Staff Monitor
        </h1>
      </div>

      {!canView ? (
        <p className="admin-page__forbidden">
          You don't have permission to view this page.
        </p>
      ) : (
        <>
          {/* Tab bar — reuses existing .tab-bar / .tab-btn conventions from admin.css */}
          <div className="tab-bar" role="tablist" aria-label="Staff Monitor tabs">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "live"}
              className={`tab-btn${activeTab === "live" ? " tab-btn--active" : ""}`}
              onClick={() => selectTab("live")}
            >
              Live
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "historical"}
              className={`tab-btn${activeTab === "historical" ? " tab-btn--active" : ""}`}
              onClick={() => selectTab("historical")}
            >
              Historical
            </button>
          </div>

          <div className="tab-content" role="tabpanel">
            {activeTab === "live" && (
              <>
                <LiveRoster />
                {/* WU-2.4 T10 — self-contained; renders nothing for viewers
                    without supervisor.queue_evict. */}
                <EvictionList />
              </>
            )}
            {activeTab === "historical" && <HistoricalReport />}
          </div>
        </>
      )}
    </div>
  );
}
